import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Divider,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  TableProps,
  Tooltip,
} from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import dayjs from "dayjs";
import { BASE_API_URL } from "../../../utils/consts";
import {
  CompanyContract,
  CompanyContractType,
} from "../../../utils/api/companyContractsAPI";
import { usePermissions } from "../../../utils/usePermissions";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  WarningOutlined,
  CalendarOutlined,
  ShopOutlined,
  UserOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { isLocationActive } from "../../../utils/helpers/locationVisibility";
import "./CompanyContracts.scss";

const CONTRACT_TYPE_LABELS: Record<CompanyContractType, string> = {
  GARDEN: "Garden / Terrace",
  PEST_CONTROL: "Pest Control (Deratization)",
  FIRE_SAFETY: "Fire Safety",
  CLEANING: "Cleaning Service",
  SECURITY: "Security",
  MAINTENANCE: "Maintenance",
  WASTE_DISPOSAL: "Waste Disposal",
  ELEVATOR: "Elevator Service",
  HVAC: "HVAC / Air Conditioning",
  OTHER: "Other",
  LEASE: "Lease",
};

const getContractStatus = (endDate: string) => {
  const days = dayjs(endDate).diff(dayjs(), "day");
  if (days < 0) return { label: "Expired", color: "default", days };
  if (days <= 7) return { label: "Expiring", color: "red", days };
  if (days <= 30) return { label: "Expiring Soon", color: "orange", days };
  return { label: "Active", color: "green", days };
};

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

const CompanyContracts: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userData = useSelector((state: any) => state.userData.user);
  const { hasPermission } = usePermissions();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 1200;

  const [contracts, setContracts] = useState<CompanyContract[]>([]);
  const [locations, setLocations] = useState<{ _id: string; name: string }[]>([]);
  const [locationFilter, setLocationFilter] = useState<string | undefined>();
  const [typeFilter, setTypeFilter] = useState<CompanyContractType | undefined>();
  const [loading, setLoading] = useState(false);
  const [showExpired, setShowExpired] = useState(false);

  const clientId = userData?.clientId;

  const fetchContracts = () => {
    if (!clientId) return;
    setLoading(true);
    axios
      .get<CompanyContract[]>(`${BASE_API_URL}/${clientId}/company-contracts`)
      .then((res) => setContracts(res.data))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!clientId) return;
    fetchContracts();
    axios
      .get(`${BASE_API_URL}/${clientId}/locations`)
      .then((res) => setLocations((res.data || []).filter(isLocationActive)))
      .catch((err) => console.error(err));
  }, [clientId]);

  const handleDelete = (contractId: string, contractName: string) => {
    Modal.confirm({
      title: t("Delete company contract?"),
      content: t("Are you sure you want to delete {{name}}?", { name: contractName }),
      okText: t("Delete"),
      okType: "danger",
      cancelText: t("Cancel"),
      onOk: () => {
        axios
          .delete(`${BASE_API_URL}/company-contract/${contractId}/delete`)
          .then(() => fetchContracts())
          .catch((err) => console.error(err));
      },
    });
  };

  const EXPIRED_THRESHOLD_DAYS = 30;

  const expiredContracts = contracts.filter(
    (c) => dayjs(c.endDate).diff(dayjs(), "day") < -EXPIRED_THRESHOLD_DAYS
  );

  const filtered = contracts.filter((c) => {
    const locId = typeof c.locationId === "object" ? c.locationId._id : c.locationId;
    if (locationFilter && locId !== locationFilter) return false;
    if (typeFilter && c.type !== typeFilter) return false;
    if (!showExpired && dayjs(c.endDate).diff(dayjs(), "day") < -EXPIRED_THRESHOLD_DAYS)
      return false;
    return true;
  });

  // Split into active (including expiring) and deeply-expired for card view
  const activeFiltered = filtered.filter(
    (c) => dayjs(c.endDate).diff(dayjs(), "day") >= -EXPIRED_THRESHOLD_DAYS
  );
  const expiredFiltered = filtered.filter(
    (c) => dayjs(c.endDate).diff(dayjs(), "day") < -EXPIRED_THRESHOLD_DAYS
  );

  // ── Desktop table columns ────────────────────────────────────────────────────
  const columns: TableProps<CompanyContract>["columns"] = [
    {
      title: t("Location"),
      key: "location",
      render: (_, record) =>
        typeof record.locationId === "object" ? record.locationId.name : record.locationId,
      sorter: (a, b) => {
        const aName = typeof a.locationId === "object" ? a.locationId.name : a.locationId;
        const bName = typeof b.locationId === "object" ? b.locationId.name : b.locationId;
        return aName.localeCompare(bName);
      },
    },
    {
      title: t("Contract Name"),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: t("Contract Type"),
      dataIndex: "type",
      key: "type",
      render: (type: CompanyContractType) => t(CONTRACT_TYPE_LABELS[type] ?? type),
    },
    {
      title: t("Supplier"),
      dataIndex: "supplier",
      key: "supplier",
      render: (v) => v || "—",
    },
    {
      title: t("Start Date"),
      dataIndex: "startDate",
      key: "startDate",
      render: (d) => dayjs(d).format("DD.MM.YYYY"),
      sorter: (a, b) => dayjs(a.startDate).unix() - dayjs(b.startDate).unix(),
    },
    {
      title: t("End Date"),
      dataIndex: "endDate",
      key: "endDate",
      render: (d) => dayjs(d).format("DD.MM.YYYY"),
      sorter: (a, b) => dayjs(a.endDate).unix() - dayjs(b.endDate).unix(),
      defaultSortOrder: "ascend",
    },
    {
      title: t("Status"),
      key: "status",
      render: (_, record) => {
        const { label, color, days } = getContractStatus(record.endDate);
        const tooltip =
          days < 0
            ? t("Expired {{count}} days ago", { count: Math.abs(days) })
            : t("Expires in {{count}} days", { count: days });
        return (
          <Tooltip title={tooltip}>
            <Tag
              color={color}
              icon={
                color !== "green" && color !== "default" ? <WarningOutlined /> : undefined
              }
            >
              {t(label)}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: t("Action"),
      key: "action",
      render: (_, record) => (
        <Space>
          {hasPermission("settings-company-contracts:update") && (
            <Button
              size="small"
              onClick={() => navigate(`/settings/company-contract/${record._id}`)}
            >
              {t("Edit")}
            </Button>
          )}
          {hasPermission("settings-company-contracts:delete") && (
            <Button
              size="small"
              danger
              onClick={() => handleDelete(record._id, record.name)}
            >
              {t("Delete")}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // ── Mobile card render ───────────────────────────────────────────────────────
  const renderCard = (c: CompanyContract) => {
    const { label, color, days } = getContractStatus(c.endDate);
    const locationName =
      typeof c.locationId === "object" ? c.locationId.name : c.locationId;
    const isExpired = days < -EXPIRED_THRESHOLD_DAYS;
    const tooltip =
      days < 0
        ? t("Expired {{count}} days ago", { count: Math.abs(days) })
        : t("Expires in {{count}} days", { count: days });

    return (
      <div key={c._id} className={`cc-card${isExpired ? " cc-card--expired" : ""}`}>
        <div className="cc-card__header">
          <div>
            <div className="cc-card__name">{c.name}</div>
            {locationName && (
              <div className="cc-card__location">
                <ShopOutlined /> {locationName}
              </div>
            )}
          </div>
          <div className="cc-card__status">
            <Tooltip title={tooltip}>
              <Tag
                color={color}
                icon={
                  color !== "green" && color !== "default" ? (
                    <WarningOutlined />
                  ) : undefined
                }
              >
                {t(label)}
              </Tag>
            </Tooltip>
          </div>
        </div>

        <div className="cc-card__meta">
          <span className="cc-card__chip">
            <TagOutlined />
            {t(CONTRACT_TYPE_LABELS[c.type] ?? c.type)}
          </span>
          {c.supplier && (
            <span className="cc-card__chip">
              <UserOutlined />
              {c.supplier}
            </span>
          )}
        </div>

        <div className="cc-card__dates">
          <span>
            <CalendarOutlined />
            <strong>{dayjs(c.startDate).format("DD.MM.YYYY")}</strong>
          </span>
          <span>→</span>
          <span>
            <strong>{dayjs(c.endDate).format("DD.MM.YYYY")}</strong>
          </span>
        </div>

        {(hasPermission("settings-company-contracts:update") ||
          hasPermission("settings-company-contracts:delete")) && (
          <div className="cc-card__actions">
            {hasPermission("settings-company-contracts:update") && (
              <Button
                size="small"
                onClick={() => navigate(`/settings/company-contract/${c._id}`)}
              >
                {t("Edit")}
              </Button>
            )}
            {hasPermission("settings-company-contracts:delete") && (
              <Button
                size="small"
                danger
                onClick={() => handleDelete(c._id, c.name)}
              >
                {t("Delete")}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Toolbar ──────────────────────────────────────────────────────────────────
  const toolbar = (
    <div className="cc-toolbar">
      {hasPermission("settings-company-contracts:create") && (
        <Button
          type="primary"
          onClick={() => navigate("/settings/company-contract/new")}
        >
          {t("Add Company Contract")}
        </Button>
      )}
      <Badge count={showExpired ? 0 : expiredContracts.length} size="small">
        <Button
          icon={showExpired ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          onClick={() => setShowExpired((v) => !v)}
          type={
            showExpired
              ? "default"
              : expiredContracts.length > 0
              ? "dashed"
              : "default"
          }
        >
          {showExpired ? t("Hide expired") : t("Show expired")}
        </Button>
      </Badge>
      <Select
        allowClear
        placeholder={t("Filter by location")}
        style={{ minWidth: 160, flex: 1 }}
        onChange={setLocationFilter}
        options={locations.map((l) => ({ value: l._id, label: l.name }))}
      />
      <Select
        allowClear
        placeholder={t("Filter by type")}
        style={{ minWidth: 180, flex: 1 }}
        onChange={(v) => setTypeFilter(v as CompanyContractType)}
        options={Object.entries(CONTRACT_TYPE_LABELS).map(([value, label]) => ({
          value,
          label: t(label),
        }))}
      />
    </div>
  );

  return (
    <>
      {toolbar}
      <Divider style={{ margin: "12px 0" }} />

      {isMobile ? (
        // ── Mobile card layout ─────────────────────────────────────────────────
        loading ? (
          <div style={{ textAlign: "center", padding: 24, color: "#9ca3af" }}>
            {t("Loading...")}
          </div>
        ) : (
          <>
            <div className="cc-cards">
              {activeFiltered.map(renderCard)}
              {activeFiltered.length === 0 && expiredFiltered.length === 0 && (
                <div style={{ textAlign: "center", padding: 32, color: "#9ca3af" }}>
                  {t("No contracts found")}
                </div>
              )}
            </div>

            {showExpired && expiredFiltered.length > 0 && (
              <div className="cc-expired-section">
                <div className="cc-expired-section__header">
                  <span>{t("Expired")} ({expiredFiltered.length})</span>
                  <div className="cc-expired-section__line" />
                </div>
                <div className="cc-cards">{expiredFiltered.map(renderCard)}</div>
              </div>
            )}
          </>
        )
      ) : (
        // ── Desktop table layout ───────────────────────────────────────────────
        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      )}
    </>
  );
};

export default CompanyContracts;
